/* The video banner's controls: play/pause and mute/unmute, bottom right, over a
   YouTube player that starts muted on a loop with its own controls off. Same
   two buttons the storefront had before the designer's copy
   (shopdot-retailer-portal GrapesEditor/videoControls.js) was split out.

   YouTube draws its own prev / pause / next overlay inside the player for a
   few seconds when playback starts, even with controls off, and nothing
   outside the iframe can style it. A black cover hides it on first load only;
   resuming from pause shows the video straight away. */

const PAUSE_ICON = '<svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><rect x="6" y="4" width="4" height="16"/><rect x="14" y="4" width="4" height="16"/></svg>';
const PLAY_ICON = '<svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><polygon points="5,3 19,12 5,21"/></svg>';
const MUTE_ICON = '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/><line x1="23" y1="9" x2="17" y2="15"/><line x1="17" y1="9" x2="23" y2="15"/></svg>';
const UNMUTE_ICON = '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/><path d="M15.54 8.46a5 5 0 0 1 0 7.07"/><path d="M19.07 4.93a10 10 0 0 1 0 14.14"/></svg>';

// YouTube player states (IFrame API): 1 playing, 2 paused.
const PLAYING = 1;
const PAUSED = 2;

// How long YouTube's start overlay stays up after playback begins.
const OVERLAY_MS = 3500;
// Lift the cover anyway if the player never reports its state.
const FALLBACK_MS = 8000;

const BUTTON_CSS = 'width:38px;height:38px;display:flex;align-items:center;justify-content:center;background:rgba(0,0,0,0.55);border:none;border-radius:50%;cursor:pointer;color:#fff;';

export function attachVideoControls(root) {
  if (!root) return;
  root.videoControlsOff?.();

  const iframe = root.querySelector('.hero-video iframe');
  if (!iframe) return;
  const frame = iframe.closest('.hero-video');
  const doc = iframe.ownerDocument || document;
  /* The player's state can land on the window holding the iframe or on the
     top window (it does in the editor canvas), so listen on both. The top is
     out of reach when the page is embedded cross-origin; the buttons then
     keep their own state. */
  const win = doc.defaultView || window;
  const wins = [win];
  try {
    if (win.top && win.top !== win && win.top.document) wins.push(win.top);
  } catch {
    // Cross-origin top.
  }

  let isPlaying = true;
  let isMuted = true;
  let lastState = null;
  let revealTimer = null;

  const cover = doc.createElement('div');
  cover.className = 'video-start-cover';
  cover.style.cssText = 'position:absolute;inset:0;z-index:2;background:#000;pointer-events:none;transition:opacity .6s ease;opacity:1;';

  const revealAfter = (ms) => {
    clearTimeout(revealTimer);
    revealTimer = setTimeout(() => {
      cover.style.opacity = '0';
    }, ms);
  };

  const wrapper = doc.createElement('div');
  wrapper.className = 'video-custom-controls';
  wrapper.style.cssText = 'position:absolute;bottom:16px;right:16px;z-index:10;display:flex;gap:8px;';

  const makeButton = () => {
    const btn = doc.createElement('button');
    btn.type = 'button';
    btn.style.cssText = BUTTON_CSS;
    return btn;
  };
  const playButton = makeButton();
  const muteButton = makeButton();

  const render = () => {
    playButton.innerHTML = isPlaying ? PAUSE_ICON : PLAY_ICON;
    playButton.setAttribute('aria-label', isPlaying ? 'Pause video' : 'Play video');
    muteButton.innerHTML = isMuted ? MUTE_ICON : UNMUTE_ICON;
    muteButton.setAttribute('aria-label', isMuted ? 'Unmute video' : 'Mute video');
  };

  const command = (func) => {
    iframe.contentWindow?.postMessage(JSON.stringify({ event: 'command', func, args: [] }), '*');
  };

  playButton.addEventListener('click', (e) => {
    e.preventDefault();
    e.stopPropagation();
    isPlaying = !isPlaying;
    command(isPlaying ? 'playVideo' : 'pauseVideo');
    render();
  });

  muteButton.addEventListener('click', (e) => {
    e.preventDefault();
    e.stopPropagation();
    isMuted = !isMuted;
    command(isMuted ? 'mute' : 'unMute');
    render();
  });

  const onMessage = (e) => {
    if (e.source !== iframe.contentWindow) return;
    let data;
    try {
      data = typeof e.data === 'string' ? JSON.parse(e.data) : e.data;
    } catch {
      return;
    }
    const state = data?.event === 'onStateChange' ? data.info : data?.info?.playerState;
    // The player repeats its state in every progress update; act on changes.
    if (typeof state !== 'number' || state === lastState) return;
    lastState = state;
    if (state === PLAYING) {
      isPlaying = true;
      // First start only: once lifted, the cover stays down.
      if (cover.style.opacity !== '0') revealAfter(OVERLAY_MS);
      render();
    } else if (state === PAUSED) {
      isPlaying = false;
      render();
    }
  };

  // Ask the player to start sending its state. Also sent now, in case the
  // iframe finished loading before this ran.
  const onLoad = () => {
    iframe.contentWindow?.postMessage(JSON.stringify({ event: 'listening', id: 1 }), '*');
  };

  wins.forEach((w) => w.addEventListener('message', onMessage));
  iframe.addEventListener('load', onLoad);
  onLoad();

  render();
  wrapper.appendChild(playButton);
  wrapper.appendChild(muteButton);
  frame.appendChild(cover);
  frame.appendChild(wrapper);
  revealAfter(FALLBACK_MS);

  root.videoControlsOff = () => {
    wins.forEach((w) => w.removeEventListener('message', onMessage));
    iframe.removeEventListener('load', onLoad);
    clearTimeout(revealTimer);
    cover.remove();
    wrapper.remove();
    root.videoControlsOff = null;
  };
}
